/**
 * "Open" for a graph node: switch to the page that owns the item and, where
 * the app has a way to, open the item itself.
 */
import { parseNodeId } from './graphModel';
import { setActiveTab } from '../store/uiModalStore';
import { openDocumentTab, openProjectsProjectTab, openTaskManagerBoardTab } from '../store/tabsStore';
import { requestTaskFocus } from '../extensions/bundled/task-manager/boardsUiStore';
import { useDocumentsUiStore } from '../extensions/bundled/documents/documentsUiStore';
import { useGraphUiStore } from './graphUiStore';
import { openExternal } from '../utils/openExternal';
import { webUrlOf } from './repoRefs';

export const PANEL_BY_TYPE = Object.freeze({
  project: 'projects.panel',
  collection: 'collections',
  folder: 'collections',
  request: 'collections',
  environment: 'collections',
  board: 'task-manager.panel',
  task: 'task-manager.panel',
  doc_folder: 'documents.panel',
  document: 'documents.panel',
  doc_workspace: 'documents.panel',
  appointment: 'calendar.panel',
  db_connection: 'db-manager.panel',
  ssh_host: 'terminal.panel',
  git_repo: 'projects.panel',
});

/**
 * The documents-store scope descriptor for a graph node (documentsStore
 * recordsContext): a team document opens in its team, a device-only one on
 * this device, anything else in the personal scope.
 */
export function documentScopeOf(node) {
  const scope = String(node?.scope || '');
  if (scope.startsWith('team:') && scope.length > 'team:'.length) return { type: 'team', teamId: scope.slice('team:'.length) };
  if (node?.location === 'device') return { type: 'device' };
  return { type: 'personal' };
}

/**
 * @param node   graph node
 * @param index  graphQueries index (used to find a task's board)
 */
export function openGraphNode(node, index) {
  const parsed = parseNodeId(node?.id);
  if (!parsed) return false;
  const panel = PANEL_BY_TYPE[parsed.type];
  if (!panel) return false;
  if (parsed.type === 'ssh_host') {
    // A saved host can be opened two ways; ask instead of guessing (owner).
    useGraphUiStore.getState().requestSshChoice(node);
    return true;
  }
  if (parsed.type === 'git_repo' && node.remote) {
    // A hosted repository opens where it lives, like a link in any browser.
    const url = webUrlOf(node.remote);
    if (url) {
      openExternal(url);
      return true;
    }
  }
  setActiveTab(panel);

  if (parsed.type === 'doc_workspace') {
    // A workspace "opens" as the Documents sidebar switched to it (card 5.4).
    useDocumentsUiStore.getState().setScopeKey(`personal/${parsed.id}`);
  } else if (parsed.type === 'document') {
    openDocumentTab({ documentId: parsed.id, label: node.label, scope: documentScopeOf(node) });
  } else if (parsed.type === 'board') {
    openTaskManagerBoardTab({ boardId: parsed.id, label: node.label });
  } else if (parsed.type === 'task') {
    const board = (index?.adj.get(node.id) || []).find(
      ({ edge }) => edge.rel === 'contains' && edge.to === node.id && edge.from.startsWith('board:')
    );
    if (board) {
      const boardNode = index.byId.get(board.edge.from);
      const boardId = board.edge.from.slice('board:'.length);
      openTaskManagerBoardTab({ boardId, label: boardNode?.label });
      requestTaskFocus(boardId, parsed.id);
    }
  } else if (parsed.type === 'project') {
    openProjectsProjectTab({ projectId: parsed.id, label: node.label });
  } else if (parsed.type === 'git_repo') {
    // A local clone has no page to open: show the project that links it.
    const holder = (index?.adj.get(node.id) || []).find(
      ({ edge }) => edge.rel === 'contains' && edge.to === node.id && edge.from.startsWith('project:')
    );
    if (holder) {
      const projectNode = index.byId.get(holder.edge.from);
      openProjectsProjectTab({ projectId: holder.edge.from.slice('project:'.length), label: projectNode?.label });
    }
  }
  return true;
}
